import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, type ReactElement } from "react";
import { CanvasFace } from "../components/CanvasFace";

// CVA4 — the capture route (register GOALS/GOALS-CANVAS-IDEA.html#cva4). The
// Desk's hidden capture window loads this route at the recorded viewport and
// theme; it renders the one shared composition through the same CanvasFace the
// board mounts (text, media and compiled slots at their placement), and
// reports on <html data-canvas-capture> when everything has drawn: the face
// at its revision, every compiled slot's frame ready and showing the asked
// build, every image decoded, fonts loaded — or why not, by name. The window
// captures on "ready" and never on a placeholder.
type Search = {
  readonly worldKey: string;
  readonly canvasId: string;
  readonly buildId: string;
  readonly width: number;
  readonly height: number;
  readonly theme: "dark" | "light";
};
export const CAPTURE_TIMEOUT_MS = 20_000;
export const Route = createFileRoute("/canvas-capture")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    worldKey: String(raw["worldKey"] ?? ""),
    canvasId: String(raw["canvasId"] ?? ""),
    buildId: String(raw["buildId"] ?? ""),
    width: Number(raw["width"]) || 960,
    height: Number(raw["height"]) || 540,
    theme: raw["theme"] === "dark" ? "dark" : "light",
  }),
  component: CanvasCapturePage,
});

/** What the page has drawn; "ready" only when nothing is still loading. */
export function captureState(root: HTMLElement, buildId: string): string | null {
  if (root.querySelector("[data-canvas-face-refusal]")) return "failed:refused";
  const face = root.querySelector("[data-canvas-face]");
  if (!face) return null;
  const slots = Array.from(root.querySelectorAll("[data-canvas-compiled-slot]"));
  if (
    slots.some((slot) =>
      ["failed", "refused", "cancelled"].includes(
        slot.getAttribute("data-canvas-compiled-slot-state") ?? "",
      ),
    )
  )
    return "failed:build";
  if (
    slots.some((slot) => {
      const b = slot.getAttribute("data-canvas-compiled-slot-build");
      return b && b !== buildId;
    })
  )
    return "failed:build-moved";
  if (!slots.every((slot) => slot.getAttribute("data-canvas-compiled-slot-frame") === "ready"))
    return null;
  const images = Array.from(root.querySelectorAll("img"));
  if (!images.every((image) => image.complete && image.naturalWidth > 0)) return null;
  return "ready";
}

function CanvasCapturePage(): ReactElement {
  const search = Route.useSearch();
  const root = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("dark", search.theme === "dark");
    html.setAttribute("data-canvas-capture", "loading");
    const started = Date.now();
    let settled = false;
    const timer = setInterval(() => {
      if (settled || !root.current) return;
      const state = captureState(root.current, search.buildId);
      if (state === "ready") {
        settled = true;
        clearInterval(timer);
        void document.fonts.ready.then(() =>
          // one frame more, so the last-drawn slot is composited before the capture
          setTimeout(() => html.setAttribute("data-canvas-capture", "ready"), 300),
        );
        return;
      }
      if (state && state.startsWith("failed:")) {
        settled = true;
        clearInterval(timer);
        html.setAttribute("data-canvas-capture", state);
        return;
      }
      if (Date.now() - started > CAPTURE_TIMEOUT_MS) {
        settled = true;
        clearInterval(timer);
        html.setAttribute("data-canvas-capture", "failed:timeout");
      }
    }, 200);
    return () => clearInterval(timer);
  }, [search.buildId, search.theme]);
  return (
    <div
      ref={root}
      className="bg-background text-foreground"
      data-canvas-capture-root={search.canvasId}
      style={{ width: search.width, minHeight: search.height }}
    >
      <CanvasFace
        worldKey={search.worldKey}
        canvasId={search.canvasId}
        captureThumbnails={false}
        presentation="bare"
      />
    </div>
  );
}
